import { Link } from "react-router-dom";
import { Phone, Mail, Globe, Clock, Instagram } from "lucide-react";
import { NAV_LINKS, SERVICES, PHONE, PHONE_LINK, EMAIL, EMAIL_LINK, WEBSITE, WEBSITE_LINK, HOURS, EMERGENCY_NOTE, INSTAGRAM_HANDLE, INSTAGRAM_LINK } from "@/data/site";

const Footer = () => (
  <footer data-testid="footer" className="border-t border-white/10 bg-black">
    <div className="mx-auto max-w-7xl px-6 py-20 md:px-10">
      <div className="grid grid-cols-1 gap-12 md:grid-cols-2 lg:grid-cols-4">
        <div>
          <Link to="/" data-testid="footer-logo" className="inline-block">
            <img
              src="/logo-footer.png"
              alt="Sage Auto Group — Your Journey. Our Priority."
              className="w-44 rounded-2xl border border-white/10"
            />
          </Link>
          <p className="mt-6 max-w-xs text-sm leading-relaxed text-zinc-500">
            Had an accident? We manage the entire process — recovery, replacement vehicles, insurance
            claims and personal injury support — so you don't have to.
          </p>
        </div>

        <div>
          <h3 className="font-mono text-xs uppercase tracking-[0.3em] text-gold">Explore</h3>
          <ul className="mt-6 space-y-3">
            {NAV_LINKS.map((link) => (
              <li key={link.to}>
                <Link
                  to={link.to}
                  data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="text-sm text-zinc-400 transition-colors duration-300 hover:text-gold"
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="font-mono text-xs uppercase tracking-[0.3em] text-gold">Services</h3>
          <ul className="mt-6 space-y-3">
            {SERVICES.map((s) => (
              <li key={s.title}>
                <Link
                  to="/services"
                  data-testid={`footer-service-${s.title.toLowerCase().replace(/\s+/g, "-")}`}
                  className="text-sm text-zinc-400 transition-colors duration-300 hover:text-gold"
                >
                  {s.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="font-mono text-xs uppercase tracking-[0.3em] text-gold">Contact</h3>
          <ul className="mt-6 space-y-4 text-sm text-zinc-400">
            <li>
              <a href={PHONE_LINK} data-testid="footer-phone" className="flex items-center gap-3 transition-colors duration-300 hover:text-gold">
                <Phone className="h-4 w-4 shrink-0 text-gold" strokeWidth={1.5} /> {PHONE}
              </a>
            </li>
            <li>
              <a href={EMAIL_LINK} data-testid="footer-email" className="flex items-center gap-3 transition-colors duration-300 hover:text-gold">
                <Mail className="h-4 w-4 shrink-0 text-gold" strokeWidth={1.5} /> {EMAIL}
              </a>
            </li>
            <li>
              <a href={WEBSITE_LINK} target="_blank" rel="noopener noreferrer" data-testid="footer-website" className="flex items-center gap-3 transition-colors duration-300 hover:text-gold">
                <Globe className="h-4 w-4 shrink-0 text-gold" strokeWidth={1.5} /> {WEBSITE}
              </a>
            </li>
            <li>
              <a href={INSTAGRAM_LINK} target="_blank" rel="noopener noreferrer" data-testid="footer-instagram" className="flex items-center gap-3 transition-colors duration-300 hover:text-gold">
                <Instagram className="h-4 w-4 shrink-0 text-gold" strokeWidth={1.5} /> {INSTAGRAM_HANDLE}
              </a>
            </li>
            <li className="flex gap-3">
              <Clock className="mt-0.5 h-4 w-4 shrink-0 text-gold" strokeWidth={1.5} />
              <div className="space-y-1">
                {HOURS.map((h) => (
                  <p key={h.days} className="text-zinc-500">{h.days}: <span className="text-zinc-300">{h.time}</span></p>
                ))}
                <p className="pt-1 text-xs text-gold/80">{EMERGENCY_NOTE}</p>
              </div>
            </li>
          </ul>
        </div>
      </div>

      <div className="mt-16 flex flex-col items-start justify-between gap-6 border-t border-white/10 pt-8 md:flex-row md:items-center">
        <p className="text-xs text-zinc-600" data-testid="footer-copyright">
          ©2026 Sage Auto Group. All rights reserved.
        </p>
        <div className="flex gap-8">
          <Link to="/privacy-policy" data-testid="footer-privacy-link" className="text-xs text-zinc-500 transition-colors duration-300 hover:text-gold">
            Privacy Policy
          </Link>
          <Link to="/terms-and-conditions" data-testid="footer-terms-link" className="text-xs text-zinc-500 transition-colors duration-300 hover:text-gold">
            Terms &amp; Conditions
          </Link>
        </div>
      </div>
    </div>
  </footer>
);

export default Footer;
